import { z } from 'zod';
import { normalTitle } from './compliance-fixes.js';
/**
 * What the compliance views say about the checks Bartizo does not fix (P11.20), in
 * the way the tools people already use for this handle it:
 *
 * - **Findings are triaged, not just counted** (Tenable's "accept risk", Qualys's
 *   "ignore", Defender's "exceptions", OpenSCAP's tailoring). A person with the
 *   authority records a status and a reason, and for anything that is a risk being
 *   carried, an expiry. Nothing is deleted: the raw score always stays visible next
 *   to the adjusted one, so an exception can never make a server *look* compliant
 *   without the page saying so.
 * - **A planned fix is not an exclusion** (Defender's "planned remediation"): it
 *   records an owner's intent and a date, and the check still counts as failing.
 * - **An exception expires** and then counts again, flagged for review.
 * - **Every failing check says why it is not automated** and what the first step
 *   is, so a long red list reads as "decisions for you", not "the server is broken".
 *
 * The notes are fixed text, here, in the build: nothing a caller sends is shown as
 * advice (rule 10's reasoning applies to anything that reads like an instruction).
 */
export const GuidanceCategory = z.enum([
    'lockout_risk',
    'disk_layout',
    'boot_change',
    'adds_software',
    'audit_rules',
    'password_policy',
    'site_policy',
    'file_permissions',
    'review',
]);
const NOTES = {
    lockout_risk: {
        label: 'Could lock you out',
        why: 'A wrong value here can stop you or your users logging in, or cut the server off the network.',
        firstStep: 'Make the change with a second login session kept open, test a fresh login, and only then close the first.',
    },
    disk_layout: {
        label: 'Needs a disk change',
        why: 'Separate partitions and mount options mean repartitioning or editing /etc/fstab, which can leave a server unable to boot.',
        firstStep: 'Plan it for a rebuild or a maintenance window. On a cloud or container host that cannot be repartitioned, mark it not applicable with a reason.',
    },
    boot_change: {
        label: 'Changes how it boots',
        why: 'This edits the bootloader and needs a restart; a mistake can leave the server unbootable.',
        firstStep: 'Take a snapshot, change /etc/default/grub and run update-grub, then restart in a maintenance window.',
    },
    adds_software: {
        label: 'Needs software installed',
        why: 'Passing means installing and running a package, which is one more thing to maintain and secure.',
        firstStep: 'Decide whether you want that package, and if so install it through your normal change process.',
    },
    audit_rules: {
        label: 'Needs audit rules',
        why: 'These pass only with auditd installed and a rule set that suits the server. Rules can be noisy and fill a disk.',
        firstStep: 'Install auditd, load a baseline rule set, and watch log volume and disk use for a week before keeping it.',
    },
    password_policy: {
        label: 'Needs your password policy',
        why: 'The right values depend on your users and rules, and tightening them can lock accounts out.',
        firstStep: 'Agree the policy first, apply it in PAM and login.defs, and test with an account you can afford to lose.',
    },
    site_policy: {
        label: 'Needs your decision',
        why: 'Wording, time servers and who may elevate to root are choices for your organisation, not defaults.',
        firstStep: 'Decide the value, then follow the check’s remediation text.',
    },
    file_permissions: {
        label: 'Safe, not automated yet',
        why: 'A plain chmod or chown that rarely breaks anything, but Bartizo does not make it for you yet.',
        firstStep: 'Run the commands in the check’s remediation text, then rescan.',
    },
    review: {
        label: 'Needs a person’s review',
        why: 'Bartizo has no safe automatic fix for this check.',
        firstStep: 'Read the check’s remediation text and apply it in a maintenance window.',
    },
};
/**
 * First match wins, so the specific go before the general: file permissions before
 * "ssh", boot before "partition".
 */
const RULES = [
    ['file_permissions', /^ensure permissions on /],
    ['boot_change', /bootloader password|enabled in the bootloader/],
    ['disk_layout', /partition|\/tmp is a separate|nodev option|nosuid option|noexec option/],
    [
        'audit_rules',
        /audit|are collected|are recorded|is collected|is immutable|events that modify|attempts to use the/,
    ],
    [
        'lockout_risk',
        /^ensure ssh |ufw|nftables|iptables|ip6tables|firewall|access to the su command/,
    ],
    [
        'password_policy',
        /password|lockout for failed|minimum days between|dictionary words|consecutive characters|changed characters/,
    ],
    [
        'adds_software',
        /is installed|systemd-journal-remote|journald is not configured to receive|chrony is enabled|ntp is enabled/,
    ],
    [
        'site_policy',
        /banner|message of the day|timeserver|^ensure sudo|cron is restricted|at is restricted|mail transfer agent|ntp |chrony|timesyncd/,
    ],
];
/**
 * Why a check that Bartizo does not fix is left to a person, and what to do first.
 * Matched by the benchmark's own title, which CIS keeps across Ubuntu releases.
 */
export function complianceGuidance(title) {
    const normal = normalTitle(title);
    const category = RULES.find(([, pattern]) => pattern.test(normal))?.[0] ?? 'review';
    return { category, ...NOTES[category] };
}
export const guidanceLabel = (category) => NOTES[category].label;
/**
 * What a person may record against one failing check on one server.
 *
 * - `accepted_risk`: the risk is understood and carried, until `expiresAt`.
 * - `not_applicable`: the check does not apply here (a cloud host that cannot be
 *   repartitioned); excluded from the adjusted score.
 * - `false_positive`: the check misjudged this server; excluded.
 * - `planned`: a fix is planned, by `expiresAt`. **Not** excluded: it still fails.
 */
export const ComplianceExceptionStatus = z.enum([
    'accepted_risk',
    'not_applicable',
    'false_positive',
    'planned',
]);
/** The statuses that take a failing check out of the adjusted score while they last. */
export const EXCEPTION_EXCLUDES = [
    'accepted_risk',
    'not_applicable',
    'false_positive',
];
/** The statuses that must carry an expiry: a risk carried or a fix promised has a date. */
export const EXCEPTION_NEEDS_EXPIRY = [
    'accepted_risk',
    'planned',
];
export const COMPLIANCE_EXCEPTION_LIMITS = {
    reasonMin: 10,
    reasonMax: 500,
    /** No exception outlives a year without somebody looking at it again. */
    maxDays: 365,
};
export const EXCEPTION_STATUS_LABEL = {
    accepted_risk: 'Accepted risk',
    not_applicable: 'Not applicable',
    false_positive: 'False positive',
    planned: 'Fix planned',
};
/** What a check carries when a person has recorded something against it. */
export const CheckException = z.object({
    id: z.uuid(),
    status: ComplianceExceptionStatus,
    reason: z.string().max(COMPLIANCE_EXCEPTION_LIMITS.reasonMax),
    /** Null for "not applicable" and "false positive" recorded without an end date. */
    expiresAt: z.iso.datetime().nullable(),
    /** The person who recorded it, as their account is named; null once it is removed. */
    by: z.string().max(320).nullable(),
    /** False once `expiresAt` has passed: it counts for nothing and is due a review. */
    active: z.boolean(),
});
/** A score at the start of a window and now, over the same servers. */
const TrendPoint = z.object({
    /** The day of the older reading. */
    at: z.iso.datetime(),
    from: z.number().int().min(0).max(100).nullable(),
    to: z.number().int().min(0).max(100).nullable(),
});
/**
 * Where a standard stands (P11.20), in checks, for the servers shown. Checks are
 * counted once per server they fail on, as the score counts them.
 */
export const CompliancePosture = z.object({
    /** Failing checks tagged to the standard. */
    failing: z.number().int().nonnegative(),
    /** Of them, those Bartizo has a safe fix for. */
    fixable: z.number().int().nonnegative(),
    /** Those with no safe fix and no accepted exception: decisions for a person. */
    needsPerson: z.number().int().nonnegative(),
    byCategory: z
        .array(z.object({ category: GuidanceCategory, count: z.number().int().positive() }))
        .max(20),
    /** Of `needsPerson`, those with a fix planned. They still count as failing. */
    planned: z.number().int().nonnegative(),
    /** Failing checks a person has taken out of the adjusted score, and why. */
    excluded: z.object({
        acceptedRisk: z.number().int().nonnegative(),
        notApplicable: z.number().int().nonnegative(),
        falsePositive: z.number().int().nonnegative(),
    }),
    /** The score with the excluded checks left out; the raw score is `score` beside it. */
    adjustedScore: z.number().int().min(0).max(100).nullable(),
    trend: z.object({
        /** Since the first reading Bartizo kept of each server. */
        since: TrendPoint.nullable(),
        /** Over the last week. */
        week: TrendPoint.nullable(),
    }),
});
export const NO_COMPLIANCE_POSTURE = {
    failing: 0,
    fixable: 0,
    needsPerson: 0,
    byCategory: [],
    planned: 0,
    excluded: { acceptedRisk: 0, notApplicable: 0, falsePositive: 0 },
    adjustedScore: null,
    trend: { since: null, week: null },
};
//# sourceMappingURL=compliance-posture.js.map